/**
 * Componenti comuni del pannello di amministrazione (restyling Asana 2).
 *
 * - AdminPageHeader: titolo, descrizione breve, azioni e filtri con la
 *   stessa gerarchia in tutti i moduli (il breadcrumb è nell'header globale).
 * - AdminBreadcrumb: percorso Gruppo › Modulo › Vista.
 * - StatusBadge: badge di stato sempre con testo (il colore non è l'unico segnale).
 * - StateBlock: stati di caricamento, assenza dati ed errore.
 * - KpiTile: indicatore numerico; se il dato manca mostra "Non disponibile"
 *   e la dipendenza, mai uno zero.
 */
import { AlertTriangle, ChevronRight, Inbox, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/button";

export const NON_DISPONIBILE = "Non disponibile";

export function formatEuro(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return NON_DISPONIBILE;
  return Number(value).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

export function formatCount(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return NON_DISPONIBILE;
  return Number(value).toLocaleString("it-IT");
}

export function AdminBreadcrumb({ items = [], className = "" }) {
  const crumbs = items.filter((item) => item && item.label);
  if (!crumbs.length) return null;
  return (
    <nav aria-label="Percorso" className={className} data-testid="admin-breadcrumb">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs font-medium text-admin-muted">
        {crumbs.map((item, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="inline-flex max-w-full items-center gap-1">
              {item.onClick && !last ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className="whitespace-normal break-words rounded text-left hover:text-admin-accent hover:underline"
                >
                  {item.label}
                </button>
              ) : (
                <span className={`whitespace-normal break-words ${last ? "text-admin-text" : ""}`} aria-current={last ? "page" : undefined}>
                  {item.label}
                </span>
              )}
              {!last ? <ChevronRight className="h-3 w-3 shrink-0" aria-hidden /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function AdminPageHeader({ icon: Icon, title, description, actions, filters, children, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid="admin-page-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[14rem]">
          <h2 className="flex items-start gap-2.5 text-xl font-semibold leading-snug text-admin-text">
            {Icon ? (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-admin-accent-soft text-admin-accent">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
            ) : null}
            <span className="min-w-0 whitespace-normal break-words">{title}</span>
          </h2>
          {description ? <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-admin-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {filters ? (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-admin-border bg-admin-surface p-3" role="group" aria-label="Filtri">
          {filters}
        </div>
      ) : null}
      {children}
    </div>
  );
}

const BADGE_TONES = {
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  accent: "border-emerald-200 bg-admin-accent-soft text-admin-accent",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  danger: "border-red-200 bg-red-50 text-red-700",
};

const DOT_TONES = {
  neutral: "bg-slate-400",
  accent: "bg-admin-accent",
  success: "bg-emerald-600",
  info: "bg-sky-600",
  warning: "bg-amber-500",
  danger: "bg-red-600",
};

export function StatusBadge({ tone = "neutral", children, title, className = "" }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${BADGE_TONES[tone] || BADGE_TONES.neutral} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${DOT_TONES[tone] || DOT_TONES.neutral}`} aria-hidden />
      {children}
    </span>
  );
}

export function StateBlock({ state = "empty", title, description, onRetry, compact = false }) {
  const config = {
    loading: { icon: Loader2, title: title || "Caricamento in corso…", spin: true, tone: "text-admin-muted" },
    empty: { icon: Inbox, title: title || "Nessun dato per i filtri selezionati", tone: "text-admin-muted" },
    error: { icon: AlertTriangle, title: title || "Impossibile caricare i dati", tone: "text-red-700" },
  }[state] || {};
  const Icon = config.icon || Inbox;
  return (
    <div
      role={state === "error" ? "alert" : "status"}
      aria-live="polite"
      className={`flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-admin-border bg-admin-surface text-center ${compact ? "p-4" : "p-8"}`}
    >
      <Icon className={`h-6 w-6 ${config.tone} ${config.spin ? "animate-spin" : ""}`} aria-hidden />
      <p className={`text-sm font-medium ${state === "error" ? "text-red-700" : "text-admin-text"}`}>{config.title}</p>
      {description ? <p className="max-w-md text-xs text-admin-muted">{description}</p> : null}
      {onRetry && state === "error" ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry} className="mt-1">
          <RefreshCw className="mr-1.5 h-4 w-4" /> Riprova
        </Button>
      ) : null}
    </div>
  );
}

export function KpiTile({ label, value, hint, unavailable = false, dependency, icon: Icon, onClick, status }) {
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium leading-snug text-admin-muted">{label}</p>
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-admin-accent" aria-hidden /> : null}
      </div>
      {unavailable ? (
        <p className="mt-2 text-base font-semibold text-admin-muted">{NON_DISPONIBILE}</p>
      ) : (
        <p className="mt-1.5 text-2xl font-semibold tabular-nums text-admin-text">{value}</p>
      )}
      {status ? <div className="mt-1.5">{status}</div> : null}
      {unavailable && dependency ? <p className="mt-1 text-xs leading-snug text-admin-muted">{dependency}</p> : null}
      {!unavailable && hint ? <p className="mt-1 text-xs leading-snug text-admin-muted">{hint}</p> : null}
    </>
  );
  const base = "block w-full rounded-xl border border-admin-border bg-admin-surface p-4 text-left shadow-sm";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${base} transition hover:border-admin-accent/50 hover:shadow`}>
        {content}
      </button>
    );
  }
  return <div className={base}>{content}</div>;
}
